import { z } from "zod";

/**
 * Where a goal's ads show (Command Center V2 phase 4, specs/formal/placements): Automatic (no spots, Meta's
 * default) or the spots the owner chose. The spots Meta offers today: Instagram Explore, Messenger Stories and
 * Facebook video feeds are gone (notes/channels/meta-placements-formats.md).
 */
export const SPOTS = {
  fb_feed: { app: "Facebook", name: "Feed", platform: "facebook", position: "feed" },
  fb_stories: { app: "Facebook", name: "Stories", platform: "facebook", position: "story" },
  fb_reels: { app: "Facebook", name: "Reels", platform: "facebook", position: "facebook_reels" },
  fb_market: { app: "Facebook", name: "Marketplace", platform: "facebook", position: "marketplace" },
  fb_search: { app: "Facebook", name: "Search results", platform: "facebook", position: "search" },
  fb_instream: { app: "Facebook", name: "In-stream video", platform: "facebook", position: "instream_video" },
  fb_right: { app: "Facebook", name: "Right column", platform: "facebook", position: "right_hand_column" },
  ig_feed: { app: "Instagram", name: "Feed", platform: "instagram", position: "stream" },
  ig_profile: { app: "Instagram", name: "Profile feed", platform: "instagram", position: "profile_feed" },
  ig_stories: { app: "Instagram", name: "Stories", platform: "instagram", position: "story" },
  ig_reels: { app: "Instagram", name: "Reels", platform: "instagram", position: "reels" },
  ig_search: { app: "Instagram", name: "Search results", platform: "instagram", position: "ig_search" },
  th_feed: { app: "Threads", name: "Feed", platform: "threads", position: "threads_stream" },
  wa_status: { app: "WhatsApp", name: "Status", platform: "whatsapp", position: "status" },
  an_apps: { app: "Audience Network", name: "Apps and sites", platform: "audience_network", position: "classic" },
  an_rewarded: { app: "Audience Network", name: "Rewarded video", platform: "audience_network", position: "rewarded_video" },
} as const;
export type Spot = keyof typeof SPOTS;
export const SPOT_KEYS = Object.keys(SPOTS) as [Spot, ...Spot[]];

/** The spots in words: "Automatic", or each spot by app and name ("Facebook Feed, Instagram Feed"). */
export const spotsText = (spots: readonly Spot[] | null | undefined) => (spots?.length ? spots.map((k) => `${SPOTS[k].app} ${SPOTS[k].name}`).join(", ") : "Automatic");

/** Meta takes these spots only with their partner (docs: placement rules), so the two move together. */
export const NEEDS_SPOT: Partial<Record<Spot, Spot>> = { th_feed: "ig_feed", wa_status: "ig_stories", ig_profile: "ig_feed" };

/** The chosen spots in the goal's Meta settings; missing means Automatic. */
export const PlacementsSchema = z
  .array(z.enum(SPOT_KEYS))
  .min(1)
  .refine((s) => new Set(s).size === s.length, "A spot is listed twice.")
  .refine((s) => s.every((k) => !NEEDS_SPOT[k] || s.includes(NEEDS_SPOT[k])), "A spot is missing the one Meta needs beside it.");
export type Placements = z.infer<typeof PlacementsSchema>;

const FIELD: Record<string, string> = {
  facebook: "facebookPositions",
  instagram: "instagramPositions",
  threads: "threadsPositions",
  whatsapp: "whatsappPositions",
  audience_network: "audienceNetworkPositions",
};

/** Zernio's top-level `placements` for the chosen spots (docs: POST /v1/ads/create, placements). */
export function toZernioPlacements(spots: readonly Spot[]): Record<string, string[]> {
  const out: Record<string, string[]> = { publisherPlatforms: [] };
  for (const k of spots) {
    const s = SPOTS[k];
    if (!out.publisherPlatforms.includes(s.platform)) out.publisherPlatforms.push(s.platform);
    (out[FIELD[s.platform]] ??= []).push(s.position);
  }
  return out;
}

const META_FIELD: Record<string, string> = {
  facebook: "facebook_positions",
  instagram: "instagram_positions",
  threads: "threads_positions",
  whatsapp: "whatsapp_positions",
  audience_network: "audience_network_positions",
};
const PLACEMENT_KEYS = ["publisher_platforms", "messenger_positions", "device_platforms", ...Object.values(META_FIELD)];

/**
 * Whether an ad set's targeting, as Meta returns it, shows the ads exactly where `spots` says (null:
 * Automatic). It answers yes only when sure: anything it cannot read as one of our spots is a no.
 */
export function metaHasSpots(targeting: Record<string, unknown> | null | undefined, spots: readonly Spot[] | null): boolean {
  const t = targeting ?? {};
  const present = PLACEMENT_KEYS.filter((k) => t[k] !== undefined);
  if (!spots) return present.length === 0;
  const want = new Set(spots.map((k) => `${SPOTS[k].platform}:${SPOTS[k].position}`));
  const platforms = t.publisher_platforms;
  if (!Array.isArray(platforms)) return false;
  if (present.some((k) => k === "messenger_positions" || k === "device_platforms")) return false;
  const got = new Set<string>();
  for (const p of platforms) {
    const field = META_FIELD[String(p)];
    const positions = field ? t[field] : undefined;
    if (!Array.isArray(positions)) return false;
    for (const pos of positions) got.add(`${String(p)}:${String(pos)}`);
  }
  return got.size === want.size && [...want].every((x) => got.has(x));
}

/** Keep a placement rule (an ad's file pinned to spots) inside the chosen spots; null when none of it is left. */
export function ruleWithin<R extends { publisherPlatforms: string[]; [field: string]: string[] }>(rule: R, spots: readonly Spot[] | null): R | null {
  if (!spots) return rule;
  const allowed = toZernioPlacements(spots);
  const out: Record<string, string[]> = { publisherPlatforms: [] };
  for (const p of rule.publisherPlatforms) {
    const field = FIELD[p];
    const keep = (rule[field] ?? []).filter((pos) => (allowed[field] ?? []).includes(pos));
    if (keep.length) {
      out.publisherPlatforms.push(p);
      out[field] = keep;
    }
  }
  return out.publisherPlatforms.length ? (out as R) : null;
}
